import React, { FC, useCallback, useRef } from 'react';

import { useIntl } from 'react-intl-next';
import { useSelector } from 'react-redux';

import { getCurrentUserLocale } from 'src/selectors/global-selectors';

import { ForgeSharedRenderer } from '../../components/ForgeSharedRenderer/ForgeSharedRenderer';
import { ForgeFctWrapperProps } from '../../types';
import { ContextToken, fetchContextTokens } from '../../utils/api';
import { getLocalId } from '../../utils/appData';
import { constructBitbucketWorkspaceAri } from '../../utils/context';

export const ForgeFctWrapper: FC<ForgeFctWrapperProps> = ({
  shouldCallGetContextToken,
  bridge,
  currentUser,
  entryPoint,
  extensions,
  extensionData,
  onInitialRender,
  onTearDown,
  workspace,
  WrapperComponent,
}) => {
  // If there are multiple apps for a given extension point, each app will
  // call the getContextToken function at the same time. But as we are fetching
  // all tokens in one call as a batch, we only want to call the fetch token
  // endpoint once. So the first app to start fetching will assign a Promise
  // to the fetchInProgress useRef which resolves once the fetching is complete
  // and the response will be accessible via the contextToken useRef.
  const fetchInProgress = useRef<Promise<void> | undefined>(undefined);
  const contextTokens = useRef<Record<string, ContextToken> | undefined>(
    undefined
  );

  const intl = useIntl();
  const accountId = currentUser?.account_id;
  const appsData = extensions.map(app => ({
    extensionId: app.id,
    appVersion: app.appVersion || '',
    localId: getLocalId(app.id, extensionData.type),
  }));
  const contextIds = [constructBitbucketWorkspaceAri(workspace.uuid)];
  const locale = useSelector(getCurrentUserLocale) || intl.locale;

  // Using stringified version of dependencies is not ideal since it's a more
  // expensive operation. However, even if the attribute values inside the below
  // objects haven't changed, a new object is referenced on a rerender which makes
  // the useCallback useless. So this is a way to prevent this by utilising the
  // stringified version as the useCallback dependency.
  // This doesn't apply to primitive values such as accountId but they have been
  // added here to merge all the dependencies into one place.
  const stringifiedDependencies = JSON.stringify({
    accountId,
    appsData,
    contextIds,
    extensionData,
  });

  const getContextTokenFnGenerator = useCallback(
    extId => async () => {
      const tokenAlreadyCached = (
        currentTokens: Record<string, ContextToken>
      ) => !!currentTokens[extId];
      // if token is gonna expire in the next 30 seconds then return true
      const expiringSoon = (
        currentTokens: Record<string, ContextToken>,
        currentTime: number
      ) =>
        parseInt(currentTokens[extId]!.expiresAt, 10) < currentTime + 30 * 1000;

      if (fetchInProgress.current) {
        await fetchInProgress.current;
      } else {
        if (
          !contextTokens.current ||
          !tokenAlreadyCached(contextTokens.current) ||
          expiringSoon(contextTokens.current, Date.now())
        ) {
          if (!accountId) throw new Error('Anonymous users not supported');

          fetchInProgress.current = new Promise((resolve, reject) => {
            fetchContextTokens({
              appsData,
              contextIds,
              workspaceId: workspace.uuid,
              extensionContext: extensionData,
            })
              .then(fetchContextTokensResponse => {
                const initialMap = contextTokens.current || {};
                contextTokens.current = fetchContextTokensResponse.reduce(
                  (map, entry) => {
                    map[entry.extensionId] = entry;
                    return map;
                  },
                  initialMap
                );
                fetchInProgress.current = undefined;
                resolve();
              })
              .catch(err => {
                reject(err);
              });
          });

          await fetchInProgress.current;
        }
      }

      return contextTokens.current![extId]!.jwt;
    },
    // eslint cannot pick up deps which have been stringified
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stringifiedDependencies]
  );

  return (
    <>
      {extensions.map(app => (
        <WrapperComponent app={app} key={app.id}>
          <ForgeSharedRenderer
            app={app}
            bridge={bridge}
            shouldCallGetContextToken={shouldCallGetContextToken}
            getContextTokenFnGenerator={getContextTokenFnGenerator}
            contextIds={contextIds}
            currentUser={currentUser}
            entryPoint={entryPoint}
            extensionData={extensionData}
            workspaceUuid={workspace.uuid}
            onInitialRender={onInitialRender}
            onTearDown={onTearDown}
            locale={locale}
          />
        </WrapperComponent>
      ))}
    </>
  );
};
